import { Fragment, useState, type MouseEvent, type ReactNode } from 'react'
import { Dropdown, Header } from '@heroui/react'
import { clampPage, PAGE_SIZES, pageSlice } from '../../lib/paging'
import { Pager } from '../../ui/Pager'
import { Icon } from '../../ui/icons'
import { useStaffText } from '../../ui/StaffText'
import { Notice } from '../kit'
import { addExclude, clearFieldFilter, colSpans, CONTEXT_MENU_CELLS, formatCell, isFiltered, rowSpans, SCREEN_ROWS, setKeep } from './layout'
import type { Cells, MeasureKind, PivotResult, PivotSpec } from './types'

export interface PivotTableProps {
  result: PivotResult
  layout: PivotSpec
  onChange: (layout: PivotSpec) => void
  label: (key: string) => string
  kind: (measure: string) => MeasureKind
  /** Someone else's or a built-in report: no cell menu that would change the layout. */
  readOnly?: boolean
}

interface Menu {
  x: number
  y: number
  items: [string, string][]
}

const NONE = /^\(.*\)$/
const SIZE_KEY = 'qms-pivot-page-size'
/** Pivot pages default to 25 rows: a grouped table reads in screenfuls, not tens. */
const DEFAULT_SIZE = 25

function readSize(): number {
  try {
    const n = Number(localStorage.getItem(SIZE_KEY))
    return (PAGE_SIZES as readonly number[]).includes(n) ? n : DEFAULT_SIZE
  } catch {
    return DEFAULT_SIZE
  }
}

/** The merged pivot table: grouped column headers, merged row cells, a Total column and a Grand total row. */
export function PivotTable({ result, layout, onChange, label, kind, readOnly = false }: PivotTableProps) {
  const { say } = useStaffText()
  const [menu, setMenu] = useState<Menu | null>(null)
  const R = result.rowFields
  const C = result.colFields
  const V = result.measures
  const [size, setSize] = useState(readSize)
  const [at, setAt] = useState({ result, page: 0 })
  const kept = result.rows.slice(0, SCREEN_ROWS)
  // A new result (another layout, filter or run) starts at its first page.
  const page = clampPage(at.result === result ? at.page : 0, kept.length, size)
  const shown = pageSlice(kept, page, size)
  // Spans per page: a group cut by a page break shows its name again at the top of the next.
  const spans = rowSpans(shown.map((r) => r.key))
  const menuOn = !readOnly && shown.length * (result.colKeys.length + 1) * Math.max(1, V.length) <= CONTEXT_MENU_CELLS
  const headRows = C.length ? C.length + 1 : 1

  const openMenu = (event: MouseEvent, rowKey: (string | null)[], colKey: string[]) => {
    if (!menuOn) return
    const items: [string, string][] = []
    rowKey.forEach((v, i) => v !== null && R[i] && items.push([R[i]!, v]))
    colKey.forEach((v, i) => C[i] && items.push([C[i]!, v]))
    if (!items.length) return
    event.preventDefault()
    setMenu({ x: event.clientX, y: event.clientY, items })
  }

  const td = (cells: Cells | undefined, rowKey: string[], colKey: string[], total = false) =>
    V.map((m) => (
      <td key={m} className="rp-n" data-total={total ? '' : undefined} onContextMenu={(e) => openMenu(e, rowKey, colKey)}>
        {formatCell(cells?.[m], kind(m))}
      </td>
    ))

  const dimCell = (value: string): ReactNode => {
    if (NONE.test(value)) return <span className="rp-muted">{value}</span>
    return value
  }

  const truncated = result.rowsTotal > kept.length
  return (
    <>
      {truncated && (
        <Notice tone="warning" className="rp-trunc">
          {say('pivot.truncated', { shown: kept.length, n: result.rowsTotal })}
        </Notice>
      )}
      <div className="rp-tw">
        <table className="rp-table-pivot" aria-label={say('pivot.result')}>
          <thead>
            <tr>
              {R.length ? (
                R.map((k) => (
                  <th key={k} rowSpan={headRows} className="rp-dimh">
                    {label(k)}
                  </th>
                ))
              ) : (
                <th rowSpan={headRows} />
              )}
              {C.length ? (
                <>
                  {colSpans(result.colKeys, 0).map((span, ci) =>
                    span === 0 ? null : (
                      <th key={ci} colSpan={span * V.length} className="rp-cg">
                        {result.colKeys[ci]![0]}
                      </th>
                    ),
                  )}
                  <th colSpan={V.length} rowSpan={C.length} className="rp-cg">
                    {say('pivot.total')}
                  </th>
                </>
              ) : (
                V.map((m) => (
                  <th key={m} className="rp-n">
                    {label(m)}
                  </th>
                ))
              )}
            </tr>
            {C.slice(1).map((_, li) => (
              <tr key={`l${li}`}>
                {colSpans(result.colKeys, li + 1).map((span, ci) =>
                  span === 0 ? null : (
                    <th key={ci} colSpan={span * V.length} className="rp-cg">
                      {result.colKeys[ci]![li + 1]}
                    </th>
                  ),
                )}
              </tr>
            ))}
            {C.length > 0 && (
              <tr>
                {[...result.colKeys, null].flatMap((_, i) =>
                  V.map((m) => (
                    <th key={`${i}-${m}`} className="rp-n">
                      {label(m)}
                    </th>
                  )),
                )}
              </tr>
            )}
          </thead>
          <tbody>
            {(R.length ? shown : [{ key: [] as string[], cells: result.grand.cells, total: result.grand.total }]).map((row, ri) => (
              <tr key={row.key.join('\u0001') || 'all'}>
                {R.length ? (
                  row.key.map((value, di) => {
                    const span = spans[ri]?.[di] ?? 1
                    if (span === 0) return null
                    const ctxKey: (string | null)[] = [...row.key.slice(0, di + 1), ...Array<null>(R.length - di - 1).fill(null)]
                    return (
                      <td key={di} className="rp-dim" rowSpan={span} onContextMenu={(e) => openMenu(e, ctxKey, [])}>
                        {dimCell(value)}
                      </td>
                    )
                  })
                ) : (
                  <td className="rp-dim">{say('pivot.allTickets')}</td>
                )}
                {C.length ? (
                  <>
                    {result.colKeys.map((ck, ci) => (
                      <Fragment key={ci}>{td(row.cells[ci], row.key, ck)}</Fragment>
                    ))}
                    {td(row.total, row.key, [], true)}
                  </>
                ) : (
                  td(row.total, row.key, [])
                )}
              </tr>
            ))}
          </tbody>
          {R.length > 0 && V.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={R.length}>{say('pivot.grandTotal')}</td>
                {C.length > 0 &&
                  result.colKeys.map((_, ci) => (
                    <Fragment key={ci}>
                      {V.map((m) => (
                        <td key={m} className="rp-n">
                          {formatCell(result.grand.cells[ci]?.[m], kind(m))}
                        </td>
                      ))}
                    </Fragment>
                  ))}
                {V.map((m) => (
                  <td key={m} className="rp-n">
                    {formatCell(result.grand.total[m], kind(m))}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {R.length > 0 && (
        <Pager
          total={kept.length}
          page={page}
          size={size}
          onPage={(p) => setAt({ result, page: p })}
          onSize={(n) => {
            setSize(n)
            setAt({ result, page: 0 })
            try {
              localStorage.setItem(SIZE_KEY, String(n))
            } catch {
              // A private window: kept for this visit only.
            }
          }}
        />
      )}
      <div className="rp-foot">
        {say(readOnly ? 'pivot.footerRead' : 'pivot.footer', { rows: R.length ? result.rowsTotal : 1, tickets: result.tickets.toLocaleString('en-GB') })}
      </div>
      {menu && (
        // A right-click menu: its trigger is an empty button placed where the click was.
        <Dropdown isOpen onOpenChange={(open) => !open && setMenu(null)}>
          <Dropdown.Trigger className="rp-cellmenu" style={{ left: menu.x, top: menu.y }} aria-label={say('pivot.cellMenu')} />
          <Dropdown.Popover placement="bottom start" className="rp-menu">
            <Dropdown.Menu
              aria-label={say('pivot.cellMenu')}
              onAction={(id) => {
                const [what, at] = String(id).split('|')
                const [key, value] = menu.items[Number(at)] ?? []
                if (key === undefined || value === undefined) return
                if (what === 'keep') onChange(setKeep(layout, key, [value]))
                else if (what === 'exclude') onChange(addExclude(layout, key, value))
                else onChange(clearFieldFilter(layout, key))
              }}
            >
              {menu.items.map(([key, value], i) => (
                <Dropdown.Section key={`${key}-${value}`} aria-label={`${label(key)}: ${value}`}>
                  <Header className="rp-menu__label">
                    {label(key)}: <b>{value}</b>
                  </Header>
                  <Dropdown.Item id={`keep|${i}`} textValue={say('pivot.keepOnly')}>
                    <Icon name="filter" size={14} />
                    {say('pivot.keepOnly')}
                  </Dropdown.Item>
                  <Dropdown.Item id={`exclude|${i}`} textValue={say('pivot.exclude')}>
                    <Icon name="close" size={14} />
                    {say('pivot.exclude')}
                  </Dropdown.Item>
                  {isFiltered(layout, key) ? (
                    <Dropdown.Item id={`all|${i}`} textValue={say('pivot.showAll')}>
                      <Icon name="refresh" size={14} />
                      {say('pivot.showAll')}
                    </Dropdown.Item>
                  ) : null}
                </Dropdown.Section>
              ))}
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      )}
    </>
  )
}
